'use client'

import { Button, ButtonVariants } from '../../../atoms'
import { ChevronDownIcon } from '../../../icons'
import {
    ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS,
    resolveIconButtonAriaLabel,
} from '../../../../constants/iconButtonAriaLabelMessageKeys'
import { DictionaryContext, useTableContext } from '../../../../providers'
import React, { ReactElement, useContext } from 'react'
import { TableCell } from './TableCell'

interface CollapseRowCellProps {
    isCollapsed: boolean
    hasContent: boolean
    toggleIsCollapsed: () => void
    /** Screen reader live region text when the row expands (polite announcement). */
    expandAnnouncement?: string
    /**
     * Id of the expanded detail row, passed to the disclosure button as `aria-controls`.
     * Must match the `regionId` on `CollapseContentCell`'s `<tr>`.
     */
    controlledRegionId?: string
}

export const CollapseRowCell = ({
    isCollapsed,
    hasContent,
    toggleIsCollapsed,
    expandAnnouncement,
    controlledRegionId,
}: CollapseRowCellProps): React.ReactElement => {
    const dictionary = useContext(DictionaryContext)
    const { columns } = useTableContext()
    const expandCollapseLabel = isCollapsed
        ? resolveIconButtonAriaLabel(dictionary, ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.TABLE_ROW_EXPAND)
        : resolveIconButtonAriaLabel(dictionary, ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.TABLE_ROW_COLLAPSE)

    return (
        <TableCell column={columns.at(-1)}>
            {hasContent && (
                <Button
                    icon={<ChevronDownIcon className={`transition-transform ${isCollapsed ? '' : 'rotate-180'}`} />}
                    variant={ButtonVariants.QUIET}
                    onClick={() => toggleIsCollapsed()}
                    data-testid="toggle-collapse-button"
                    aria-expanded={!isCollapsed}
                    aria-controls={controlledRegionId}
                    aria-label={expandCollapseLabel}
                />
            )}
            {hasContent && !isCollapsed && (
                <div role="status" aria-live="polite" aria-atomic className="sr-only">
                    {expandAnnouncement ?? ''}
                </div>
            )}
        </TableCell>
    )
}
